import type { JSX } from 'react'
import { useState } from 'react'
import { Box, TextField, Button } from '@mui/material'

/**
 * Message composer: a controlled multi-line TextField + Send/Stop button.
 *
 * - Enter (without Shift) submits the message.
 * - Shift+Enter inserts a newline.
 * - Empty / whitespace-only submissions are silently ignored.
 * - The field is cleared after a successful send.
 * - While `pending`, the TextField is disabled and the Send button is
 *   replaced by an always-enabled Stop button (calls `onStop`).
 */
export function Composer({
  onSend,
  onStop,
  pending,
}: {
  onSend: (text: string) => void
  onStop: () => void
  pending?: boolean
}): JSX.Element {
  const [value, setValue] = useState('')

  const submit = () => {
    const trimmed = value.trim()
    if (!trimmed) return
    onSend(trimmed)
    setValue('')
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        p: 1.5,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <TextField
        fullWidth
        multiline
        maxRows={4}
        size="small"
        placeholder="Ask the advisor…"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            submit()
          }
        }}
        disabled={pending}
      />
      {pending ? (
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          onClick={onStop}
          sx={{ alignSelf: 'flex-end', whiteSpace: 'nowrap' }}
        >
          Stop
        </Button>
      ) : (
        <Button
          variant="contained"
          size="small"
          onClick={submit}
          disabled={!value.trim()}
          sx={{ alignSelf: 'flex-end', whiteSpace: 'nowrap' }}
        >
          Send
        </Button>
      )}
    </Box>
  )
}
